import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Camera, FileText, Heart, Sliders, Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppHeader } from '@/components/layout/AppHeader';
import { PhotoUpload } from '@/components/profile/PhotoUpload';

const MAX_CHARS = 500;

interface PhotoData {
  url: string;
  order: number;
  scanStatus: 'pending' | 'safe' | 'warning' | 'blocked';
}

interface LifestylePreferences {
  socialLevel?: number;
  weekendEnergy?: number;
  datingPace?: number;
  communicationStyle?: number;
}

interface Profile {
  id: string;
  userId: string;
  displayName: string;
  age: number;
  bio: string | null;
  lookingFor: string | null;
  photos: PhotoData[];
  lifestylePreferences: LifestylePreferences | null;
  aiPersonalityVector: any;
  aiProcessedAt: string | null;
  profileCompletion: number;
  createdAt: string;
  updatedAt: string;
}

interface ProfileResponse {
  profile: Profile;
  type: 'teacher' | 'non_educator';
}

// Helper function to get lifestyle label
function getLifestyleLabel(value: number | undefined, type: 'social' | 'weekend' | 'pace' | 'communication'): string {
  if (value === undefined) return 'Not set';

  const labels = {
    social: ['Introvert', 'Moderate Introvert', 'Moderate Extrovert', 'Extrovert'],
    weekend: ['Homebody', 'Mostly Relaxed', 'Mostly Active', 'Always Out'],
    pace: ['Very Slow', 'Slow & Steady', 'Moderate', 'Fast-Paced'],
    communication: ['Brief & Casual', 'Balanced', 'Conversational', 'Deep & Chatty'],
  };

  const categoryLabels = labels[type];
  if (value <= 25) return categoryLabels[0];
  if (value <= 50) return categoryLabels[1];
  if (value <= 75) return categoryLabels[2];
  return categoryLabels[3];
}

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  // Form state
  const [bio, setBio] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [socialEnergy, setSocialEnergy] = useState(50);
  const [weekendEnergy, setWeekendEnergy] = useState(50);
  const [datingPace, setDatingPace] = useState(50);
  const [communicationStyle, setCommunicationStyle] = useState(50);
  const [uploadedPhotos, setUploadedPhotos] = useState<{ id: string; key: string; status: string }[]>([]);

  // Fetch profile data
  const { data, isLoading, error } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        // No profile exists - redirect to onboarding
        console.log('[ProfileEdit] No profile found, redirecting to onboarding');
        navigate('/profile/photos');
        return null;
      }

      if (!response.ok) throw new Error('Failed to fetch profile');
      return response.json() as Promise<ProfileResponse>;
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (data?.profile) {
      setBio(data.profile.bio || '');
      setLookingFor(data.profile.lookingFor || '');
      if (data.profile.lifestylePreferences) {
        setSocialEnergy(data.profile.lifestylePreferences.socialLevel || 50);
        setWeekendEnergy(data.profile.lifestylePreferences.weekendEnergy || 50);
        setDatingPace(data.profile.lifestylePreferences.datingPace || 50);
        setCommunicationStyle(data.profile.lifestylePreferences.communicationStyle || 50);
      }
    }
  }, [data]);

  const handlePhotosUploaded = (photos: { id: string; key: string; status: string }[]) => {
    console.log('Photos updated:', photos);
    setUploadedPhotos(photos);
  };

  // Save profile mutation
  const saveProfileMutation = useMutation({
    mutationFn: async (profileData: {
      bio: string;
      lookingFor: string;
      photos?: { url: string; order: number; scanStatus: 'pending' | 'safe' | 'warning' | 'blocked' }[];
      lifestylePreferences: {
        socialLevel: number;
        weekendEnergy: number;
        datingPace: number;
        communicationStyle: number;
      };
    }) => {
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/profiles/me`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
      if (!response.ok) throw new Error('Failed to save profile');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'me'] });
      toast.success('Profile updated successfully!');
    },
    onError: (error) => {
      console.error('Save error:', error);
      toast.error('Failed to update profile. Please try again.');
    },
  });

  const handleSave = async () => {
    // Validation
    if (!bio.trim()) {
      toast.error('Please write something about yourself');
      return;
    }

    if (!lookingFor.trim()) {
      toast.error('Please describe what you\'re looking for');
      return;
    }

    if (bio.length > MAX_CHARS) {
      toast.error(`Bio must be ${MAX_CHARS} characters or less`);
      return;
    }

    if (lookingFor.length > MAX_CHARS) {
      toast.error(`"Looking For" must be ${MAX_CHARS} characters or less`);
      return;
    }

    const profileUpdate: any = {
      bio,
      lookingFor,
      lifestylePreferences: {
        socialLevel: socialEnergy,
        weekendEnergy,
        datingPace,
        communicationStyle,
      },
    };

    // Include photos if they were updated
    if (uploadedPhotos.length > 0) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const baseUrl = import.meta.env.PROD
        ? `${apiUrl}/api/photos/serve`
        : 'http://localhost:8787/api/photos/serve';
      profileUpdate.photos = uploadedPhotos.map((p, index) => ({
        url: `${baseUrl}/${p.key}`,
        order: index + 1,
        scanStatus: p.status as 'pending' | 'safe' | 'warning' | 'blocked',
      }));
    }

    await saveProfileMutation.mutateAsync(profileUpdate);
  };

  const handleCancel = () => {
    navigate('/feed');
  };

  if (isLoading) {
    return (
      <>
        <AppHeader />
        <div className="min-h-screen bg-brand-background flex items-center justify-center pt-16">
          <Loader2 className="h-12 w-12 animate-spin text-brand-primary" />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <AppHeader />
        <div className="min-h-screen bg-brand-background flex items-center justify-center p-4 pt-16">
          <Card className="w-full max-w-md shadow-brand-glow bg-brand-surface border-brand-border/50">
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-lg text-red-400 mb-2">Failed to load profile</p>
              <p className="text-sm text-gray-400">
                {error instanceof Error ? error.message : 'Profile not found. You may need to complete onboarding first.'}
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/feed')} variant="ghost" className="text-gray-400 hover:text-white">
                  Back to Feed
                </Button>
                <Button onClick={() => navigate('/profile/photos')} className="bg-brand-primary hover:bg-brand-primary/90 text-white">
                  Complete Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const { profile } = data;
  // Show all photos - scan warnings during upload are sufficient
  const safePhotos = profile.photos;

  return (
    <>
      <AppHeader />
      <div className="min-h-screen bg-brand-background py-8 px-4 pt-20">
        <div className="w-full max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Edit Profile</h1>
            <p className="text-gray-400">Update your profile information</p>
          </div>

          {/* Photos Section */}
          <Card className="shadow-brand-glow bg-brand-surface border-brand-border/50 glass-light">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">Photos</CardTitle>
                <Badge variant="secondary" className="ml-2 bg-brand-primary/20 text-brand-primary">
                  {safePhotos.length} / 5
                </Badge>
              </div>
              <CardDescription className="text-gray-400">
                Upload up to 5 photos to showcase your personality
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PhotoUpload
                initialPhotos={profile.photos}
                onPhotosUploaded={handlePhotosUploaded}
                maxPhotos={5}
                showSafetyBadges={true}
              />
            </CardContent>
          </Card>

          {/* Bio Section */}
          <Card className="shadow-brand-glow bg-brand-surface border-brand-border/50 glass-light">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">About Me</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Tell others about yourself ({bio.length}/{MAX_CHARS} characters)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Share your interests, hobbies, what makes you unique..."
                className="min-h-[120px] bg-brand-background border-brand-border text-white placeholder:text-gray-500"
                maxLength={MAX_CHARS}
              />
            </CardContent>
          </Card>

          {/* Looking For Section */}
          <Card className="shadow-brand-glow bg-brand-surface border-brand-border/50 glass-light">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">What I'm Looking For</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Describe your ideal match ({lookingFor.length}/{MAX_CHARS} characters)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={lookingFor}
                onChange={(e) => setLookingFor(e.target.value)}
                placeholder="What qualities, values, or interests are you looking for in a partner?"
                className="min-h-[120px] bg-brand-background border-brand-border text-white placeholder:text-gray-500"
                maxLength={MAX_CHARS}
              />
            </CardContent>
          </Card>

          {/* Lifestyle Preferences Section */}
          <Card className="shadow-brand-glow bg-brand-surface border-brand-border/50 glass-light">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sliders className="h-5 w-5 text-brand-primary" />
                <CardTitle className="text-xl text-white">Lifestyle Preferences</CardTitle>
              </div>
              <CardDescription className="text-gray-400">
                Help us understand your daily rhythms and social energy
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Social Energy */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="socialEnergy" className="text-base font-semibold text-white">
                    Social Energy
                  </Label>
                  <span className="text-sm font-medium text-brand-primary">
                    {getLifestyleLabel(socialEnergy, 'social')}
                  </span>
                </div>
                <Slider
                  id="socialEnergy"
                  min={0}
                  max={100}
                  step={1}
                  value={[socialEnergy]}
                  onValueChange={(value) => setSocialEnergy(value[0])}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Introvert</span>
                  <span>Extrovert</span>
                </div>
              </div>

              {/* Weekend Energy */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="weekendEnergy" className="text-base font-semibold text-white">
                    Weekend Energy
                  </Label>
                  <span className="text-sm font-medium text-brand-primary">
                    {getLifestyleLabel(weekendEnergy, 'weekend')}
                  </span>
                </div>
                <Slider
                  id="weekendEnergy"
                  min={0}
                  max={100}
                  step={1}
                  value={[weekendEnergy]}
                  onValueChange={(value) => setWeekendEnergy(value[0])}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Homebody</span>
                  <span>Always Out</span>
                </div>
              </div>

              {/* Dating Pace */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="datingPace" className="text-base font-semibold text-white">
                    Dating Pace
                  </Label>
                  <span className="text-sm font-medium text-brand-primary">
                    {getLifestyleLabel(datingPace, 'pace')}
                  </span>
                </div>
                <Slider
                  id="datingPace"
                  min={0}
                  max={100}
                  step={1}
                  value={[datingPace]}
                  onValueChange={(value) => setDatingPace(value[0])}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Take It Slow</span>
                  <span>Fast-Paced</span>
                </div>
              </div>

              {/* Communication Style */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="communicationStyle" className="text-base font-semibold text-white">
                    Communication Style
                  </Label>
                  <span className="text-sm font-medium text-brand-primary">
                    {getLifestyleLabel(communicationStyle, 'communication')}
                  </span>
                </div>
                <Slider
                  id="communicationStyle"
                  min={0}
                  max={100}
                  step={1}
                  value={[communicationStyle]}
                  onValueChange={(value) => setCommunicationStyle(value[0])}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Brief & Casual</span>
                  <span>Deep & Chatty</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card className="shadow-brand-glow bg-brand-surface border-brand-border/50 glass-light">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-4">
                <Button
                  onClick={handleCancel}
                  variant="ghost"
                  className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full px-6"
                  disabled={saveProfileMutation.isPending}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>

                <Button
                  onClick={handleSave}
                  disabled={saveProfileMutation.isPending}
                  className="bg-brand-primary hover:bg-brand-primary/90 hover:scale-105 text-white flex items-center gap-2 px-8 rounded-full shadow-lg shadow-brand-primary/20 transition-transform"
                >
                  {saveProfileMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
